import React, { useState, useEffect, useRef } from 'react';
import './Header.css';
import { FaMapMarkerAlt, FaSpinner, FaChevronDown } from 'react-icons/fa';

const Header = ({ location, onDetectLocation, isDetectingLocation, locationError }) => {
  // Smooth scroll function
  const scrollToSection = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 80; // Height of fixed header
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerOffset;
      
      // Use instant scroll on mobile for better UX, smooth on desktop
      const behavior = window.innerWidth <= 768 ? 'instant' : 'smooth';
      
      window.scrollTo({
        top: offsetPosition,
        behavior: behavior
      });
      
      // Update URL without adding to history
      window.history.pushState(null, null, `#${id}`);
    }
  };
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLocationMenu, setShowLocationMenu] = useState(false);
  const menuRef = useRef(null);
  const menuBtnRef = useRef(null);
  const locationRef = useRef(null);

  const fetchLocation = async () => {
    await onDetectLocation?.();
    setShowLocationMenu(true);
  };

  // Close location menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationRef.current && !locationRef.current.contains(event.target)) {
        setShowLocationMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && 
          menuBtnRef.current && !menuBtnRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = (e, id) => {
    setIsMenuOpen(false);
    if (id) {
      e.preventDefault();
      
      // Ensure menu is visually closed before scrolling
      // Use a slightly longer delay on mobile to ensure CSS transitions complete
      const delay = window.innerWidth <= 768 ? 200 : 100;
      
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          const headerOffset = 80; // Match the header height
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - headerOffset;
          
          // Use instant scroll on mobile for better UX, smooth on desktop
          const behavior = window.innerWidth <= 768 ? 'instant' : 'smooth';
          
          window.scrollTo({
            top: offsetPosition,
            behavior: behavior
          });
          
          // Update URL without adding to history
          window.history.pushState(null, null, `#${id}`);
        }
      }, delay);
    }
  };

  return (
    <>
      <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="headerInner">
          <div className="brand">
            <div className="brandContainer">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', position: 'relative' }}>
                <div style={{
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'flex-end',
                  marginBottom: '2px'
                }}>
                  <video 
                    autoPlay 
                    loop 
                    muted 
                    playsInline 
                    style={{
                      width: '100px',
                      height: '100px',
                      objectFit: 'contain',
                      objectPosition: 'bottom',
                      pointerEvents: 'none',
                      marginRight: '5px',
                      position: 'relative',
                      bottom: '0',
                      transform: 'none'
                    }}
                  >
                    <source src="/santa.webm" type="video/webm" />
                  </video>
                </div>
                <button
                  type="button"
                  className="brandLink" 
                  onClick={(e) => {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    closeMenu();
                  }}
                >
                  <img 
                    src="/logo.png" 
                    alt="Menu Bazaar" 
                    className="brandLogoSolo" 
                  />
                </button>
              </div>
              
              <div className="locationContainer" ref={locationRef}>
            <button 
              className="locationButton" 
              onClick={() => {
                fetchLocation();
              }}
              aria-label="Set your location"
            >
              <FaMapMarkerAlt className="locationIcon" />
              <span className="locationText">
                {isDetectingLocation ? 'Detecting...' : (location?.name || 'Set Location')}
              </span>
              {!isDetectingLocation && <FaChevronDown className="chevronIcon" />}
              {isDetectingLocation && <FaSpinner className="spinnerIcon" />}
            </button>

            {showLocationMenu && location && (
              <div className="locationMenu">
                <div className="locationHeader">
                  <FaMapMarkerAlt className="menuLocationIcon" />
                  <div>
                    <h4>Current Location</h4>
                    <p className="locationAddress">{location.address}</p>
                    {locationError && (
                      <p className="locationAddress" style={{ marginTop: 6, color: '#ffb3b3' }}>
                        {locationError}
                      </p>
                    )}
                  </div>
                </div>
                <div className="locationActions">
                  <button 
                    className="actionButton primary"
                    onClick={() => {
                      fetchLocation();
                      setShowLocationMenu(false);
                    }}
                  >
                    Refresh Location
                  </button>
                </div>
              </div>
            )}
              </div>
            </div>
          </div>

          <div className="headerRight">
            <button 
              className={`menuBtn ${isMenuOpen ? 'isOpen' : ''}`} 
              aria-label="Menu" 
              onClick={toggleMenu}
              ref={menuBtnRef}
            >
              <div className="menuBars">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </button>
            
            <nav className={`nav ${isMenuOpen ? 'mobileNavOpen' : ''}`} ref={menuRef}>
              <a href="#top" className="navLink" onClick={(e) => closeMenu(e, 'top')}>
                <i className="fas fa-home"></i> Home
              </a>
              <a href="#services" className="navLink" onClick={(e) => closeMenu(e, 'services')}>
                <i className="fas fa-cogs"></i> Services
              </a>
              <a href="#city" className="navLink" onClick={(e) => closeMenu(e, 'city')}>
                <i className="fas fa-map-marker-alt"></i> City
              </a>
              <a href="#choose" className="navLink navCta" onClick={(e) => closeMenu(e, 'choose')}>
                <i className="fas fa-rocket"></i> Choose
              </a>
            </nav>
          </div>
        </div>
      </header>
      
      {/* Overlay that appears when mobile menu is open */}
      <div 
        className={`menuOverlay ${isMenuOpen ? 'active' : ''}`}
        onClick={closeMenu}
      />
    </>
  );
}

export default Header;
